<template>
  <div>
    <div class="section-head d-flex justify-content-between align-items-center mt-4 mb-2">
      <span class="section-title">
        {{ $l('table_label_product_inspection_photo_section') || '产品验货照片' }}
      </span>
    </div>

    <template v-if="reportRowLength === 0">
      <div class="empty-hint text-muted py-3">
        {{
          $l('tips_inspection_report_no_product') ||
            '暂无产品信息，请先在「验货明细及资料」中维护产品后再上传照片。'
        }}
      </div>
    </template>

    <template v-else>
      <div v-for="block in productBlocks" :key="block.inspectionProductCode">
        <div
          v-for="row in block.rows"
          :key="row.inspectionProductDetailCode"
          class="product-block mb-4"
        >
          <el-form
            :model="row"
            label-width="100px"
            size="small"
            class="product-info-form"
          >
            <el-row :gutter="0" class="info-grid-row">
              <el-col :span="8" class="info-grid-cell">
                <el-form-item :label="$l('form_label_orderNo_short') || '订单编号'">
                  <span>{{ row.orderNo || '--' }}</span>
                </el-form-item>
              </el-col>
              <el-col :span="16" class="info-grid-cell">
                <el-form-item :label="$l('form_label_identifyGoodsNo') || '产品识别号'">
                  <div class="id-value-row">
                    <span class="text-break">{{ row.identifyGoodsNo || '--' }}</span>
                    <el-button
                      size="small"
                      plain
                      :disabled="disabled"
                      class="ml-auto"
                      @click="openPhotoDialog(row)"
                    >
                      {{ $l('btn_upload_inspection_photo') || '上传照片' }}
                    </el-button>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="0" class="info-grid-row">
              <el-col :span="8" class="info-grid-cell">
                <el-form-item :label="$l('form_label_orderGoodsColor') || '颜色'">
                  <span>{{ row.orderGoodsColor || '--' }}</span>
                </el-form-item>
              </el-col>
              <el-col :span="8" class="info-grid-cell">
                <el-form-item :label="$l('form_label_size') || '尺寸'">
                  <span>{{ row.orderGoodsEnglishSize || '--' }}</span>
                </el-form-item>
              </el-col>
              <el-col :span="4" class="info-grid-cell">
                <el-form-item
                  :label="$l('form_label_orderTotalQty_short') || '订单数量'"
                >
                  <span>{{ row.orderTotalQty || '--' }}</span>
                </el-form-item>
              </el-col>
              <el-col :span="4" class="info-grid-cell">
                <el-form-item :label="$l('form_label_finishedQty') || '完成数量'">
                  <el-input
                    v-model.number="row.completedQty"
                    type="number"
                    :min="0"
                    :disabled="disabled"
                    size="small"
                    @blur="onCompletedQtyBlur(row)"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>

          <div class="photo-strip d-flex">
            <div
              v-for="(img, idx) in displayImages(row)"
              :key="img.inspectionProductImageCode || idx"
              class="photo-item"
            >
              <el-image
                :src="img.imageUrl"
                fit="cover"
                class="photo-thumb"
                :preview-src-list="[img.imageUrl]"
              />
              <div
                class="photo-desc text-muted small text-truncate"
                @dblclick="openInlineImageDescDialog(row, img)"
              >
                {{
                  img.imageDesc || '--'
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <el-dialog
      :title="$l('dialog_title_inspection_photo_upload') || '上传产品验货照片'"
      :visible.sync="photoDialogVisible"
      width="920px"
      custom-class="inspection-photo-dialog"
      append-to-body
      :close-on-click-modal="false"
      @closed="onPhotoDialogClosed"
    >
      <div class="photo-dialog-toolbar mb-3">
        <el-button
          size="small"
          type="primary"
          plain
          :disabled="disabled || !photoDialogRow"
          @click="triggerSinglePhotoUpload"
        >
          {{ $l('btn_add_single_photo') || '选择图片上传' }}
        </el-button>
        <el-upload
          ref="singlePhotoUploader"
          class="d-none"
          action="#"
          multiple
          accept="image/*"
          :show-file-list="false"
          :auto-upload="false"
          :on-change="onSinglePhotosChangeDebounced"
        >
          <el-button ref="singlePhotoBtn" slot="trigger" class="d-none" />
        </el-upload>
      </div>

      <div class="photo-dialog-grid d-flex flex-wrap">
        <div
          v-for="(img, idx) in dialogImageList"
          :key="img.inspectionProductImageCode || img.imageCode"
          class="photo-dialog-item"
        >
          <div class="photo-dialog-img-wrap">
            <el-image
              :src="img.imageUrl"
              fit="cover"
              class="photo-dialog-img"
              :preview-src-list="dialogPreviewUrls"
              :initial-index="idx"
            />
            <i
              v-if="!disabled"
              class="el-icon-close photo-del-btn"
              @click="removeDialogImage(img)"
            />
          </div>
          <el-input
            v-model="img.imageDesc"
            type="textarea"
            :rows="2"
            maxlength="100"
            size="small"
            :placeholder="
              $l('placeholder_photo_desc') ||
                '请填写照片描述文字，鼠标移开自动保存...'
            "
            @blur="saveImageDesc(img)"
          />
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :title="$l('dialog_title_edit') || '编辑描述'"
      :visible.sync="inlineDescDialogVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      @closed="onInlineDescDialogClosed"
    >
      <el-input
        v-model="inlineDescText"
        type="textarea"
        :rows="4"
        maxlength="100"
        show-word-limit
        :placeholder="$l('placeholder_photo_desc') || '照片描述文字'"
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="inlineDescDialogVisible = false">
          {{ $l('INDEX.btn_cancel') || '取消' }}
        </el-button>
        <el-button type="primary" :disabled="disabled" @click="confirmInlineImageDesc">
          {{ $l('INDEX.btn_confirm') || '确定' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import EditLoading from '@/mixins/edit-loading'

export default {
  name: 'InspectionDetailReportProductDetailImage',
  mixins: [EditLoading],
  props: {
    inspectionApplyCode: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    dialogPreviewUrls() {
      return (this.dialogImageList || [])
        .map((i) => i && i.imageUrl)
        .filter(Boolean)
    },
  },
  data() {
    return {
      reportRowLength: 0,
      productBlocks: [],
      photoDialogVisible: false,
      photoDialogRow: null,
      dialogImageList: [],
      inlineDescDialogVisible: false,
      inlineDescText: '',
      inlineDescRow: null,
      inlineDescImg: null,
      singlePhotoUploadTimer: null,
    }
  },
  watch: {
    inspectionApplyCode: {
      immediate: true,
      handler(val) {
        if (val) this.loadProductPhotos()
        else {
          this.productBlocks = []
          this.reportRowLength = 0
        }
      },
    },
  },
  methods: {
    ensureOk(res, fallbackMsg) {
      if (!res) {
        this.$message.error(fallbackMsg || '接口调用失败')
        return false
      }
      if (res.status !== 200) {
        this.$message.error(res.msg || fallbackMsg || '接口调用失败')
        return false
      }
      return true
    },
    parseListData(res) {
      const body = res && res.body
      if (!body) return []
      return Array.isArray(body.data) ? body.data : []
    },
    displayImages(row) {
      const list = row.imageList || []
      return list
    },
    async loadProductPhotos() {
      if (!this.inspectionApplyCode) {
        this.productBlocks = []
        this.reportRowLength = 0
        return
      }
      const pres = await this.$api
        .dfInspectionProductDetailApi({
          inspectionApplyCode: this.inspectionApplyCode,
        })
        .catch(() => null)
      if (
        !this.ensureOk(
          pres,
          this.$l('tips_inspection_photo_product_list_fail') || '获取产品信息失败',
        )
      ) {
        this.productBlocks = []
        this.reportRowLength = 0
        return
      }
      const product = (pres && pres.body) || {}
      const code = product.inspectionProductCode
      if (!code) {
        this.productBlocks = []
        this.reportRowLength = 0
        return
      }
      const r = await this.$api
        .dfInspectionProductDetailReportListApi({
          inspectionProductCode: code,
        })
        .catch(() => null)
      if (
        !this.ensureOk(
          r,
          this.$l('tips_inspection_photo_list_fail') || '获取产品照片失败',
        )
      ) {
        this.productBlocks = []
        this.reportRowLength = 0
        return
      }
      const rows = this.parseListData(r).map((row) => ({
        ...row,
        completedQty: this.normalizeNonNegativeNumber(row.completedQty),
        imageList: row.imageList || [],
      }))
      this.productBlocks = [
        {
          inspectionProductCode: code,
          productName: product.productName,
          rows,
        },
      ]
      this.reportRowLength = rows.length
    },
    openPhotoDialog(row) {
      this.photoDialogRow = { ...row }
      this.dialogImageList = JSON.parse(JSON.stringify(row.imageList || []))
      this.photoDialogVisible = true
    },
    onPhotoDialogClosed() {
      this.photoDialogRow = null
      this.dialogImageList = []
      this.loadProductPhotos()
    },
    triggerSinglePhotoUpload() {
      if (this.disabled || !this.photoDialogRow) return
      this.$refs.singlePhotoBtn.$el.click()
    },
    maxSortNo(list) {
      if (!list || !list.length) return 0
      return Math.max(
        0,
        ...list.map((i) =>
          typeof i.sortNo === 'number' ? i.sortNo : parseInt(i.sortNo, 10) || 0,
        ),
      )
    },
    normalizeNonNegativeNumber(val) {
      const n = Number(val)
      if (Number.isNaN(n)) return 0
      return n < 0 ? 0 : n
    },
    async onCompletedQtyBlur(row) {
      if (this.disabled || !row || !row.inspectionProductDetailCode) return
      const newVal = this.normalizeNonNegativeNumber(row.completedQty)
      row.completedQty = newVal

      const confirm = await this.$confirm(
        this.$l('tips_confirm_change_completed_qty') || '是否确定更改完成数量？',
        this.$l('dialog_title_confirm') || '确认信息',
        {
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          type: 'warning',
        },
      )
        .then(() => true)
        .catch(() => false)

      if (!confirm) return

      const res = await this.$api
        .dfInspectionProductDetailReportSaveApi({
          inspectionProductDetailCode: row.inspectionProductDetailCode,
          completedQty: newVal,
        })
        .catch(() => null)

      if (
        !this.ensureOk(
          res,
          this.$l('tips_save_completed_qty_fail') || '完成数量更新失败',
        )
      ) {
        return
      }
      this.$message.success(
        this.$l('tips_save_completed_qty_ok') || '完成数量更新成功',
      )
    },
    onSinglePhotosChangeDebounced() {
      clearTimeout(this.singlePhotoUploadTimer)
      this.singlePhotoUploadTimer = setTimeout(() => {
        const uploader = this.$refs.singlePhotoUploader
        const fileList = (uploader && uploader.uploadFiles) || []
        const rawFiles = fileList.map((f) => f.raw).filter(Boolean)
        if (!rawFiles.length) return
        this.processSinglePhotoFiles(rawFiles)
        uploader.clearFiles()
      }, 300)
    },
    async processSinglePhotoFiles(rawFiles = []) {
      if (!rawFiles.length || !this.photoDialogRow) return
      if (this.loadingOpen()) return
      const row = this.photoDialogRow
      let sortNo = this.maxSortNo(this.dialogImageList)
      let successCount = 0
      for (const raw of rawFiles) {
        const res = await this.$api.SYS_UPLOAD(raw).catch(() => null)
        if (!this.ensureOk(res, this.$l('tips_upload_fail') || '上传失败')) continue
        if (!res.body) continue
        sortNo += 1
        const payload = {
          inspectionProductImageCode: '',
          inspectionProductDetailCode: row.inspectionProductDetailCode,
          inspectionProductCode: row.inspectionProductCode,
          inspectionApplyCode: row.inspectionApplyCode || this.inspectionApplyCode,
          sortNo,
          imageCode: res.body.fileId,
          imageUrl: res.body.url,
          imageDesc: '',
        }
        const saveRes = await this.$api
          .dfInspectionProducImageSaveApi(payload)
          .catch(() => null)
        if (!saveRes || saveRes.status !== 200) {
          this.$message.error(
            this.$l('tips_inspection_photo_save_fail') || '保存失败',
          )
          continue
        }
        successCount += 1
      }
      this.loadingClose()
      if (!successCount) {
        return
      }
      const listRes = await this.$api
        .dfInspectionProductDetailReportListApi({
          inspectionProductCode: row.inspectionProductCode,
        })
        .catch(() => null)
      if (
        !this.ensureOk(
          listRes,
          this.$l('tips_inspection_photo_list_fail') || '获取产品照片失败',
        )
      ) {
        return
      }
      const rows = this.parseListData(listRes)
      const fresh = rows.find(
        (r) => r.inspectionProductDetailCode === row.inspectionProductDetailCode,
      )
      if (fresh && fresh.imageList) {
        this.dialogImageList = JSON.parse(JSON.stringify(fresh.imageList))
        this.photoDialogRow = { ...fresh }
      }
      this.$message.success(this.$l('tips_inspection_photo_save_ok') || '上传成功')
    },
    async saveImageDesc(img) {
      if (!this.photoDialogRow || this.disabled) return
      const payload = {
        inspectionProductImageCode: img.inspectionProductImageCode || '',
        inspectionProductDetailCode: this.photoDialogRow.inspectionProductDetailCode,
        inspectionProductCode: this.photoDialogRow.inspectionProductCode,
        inspectionApplyCode:
          this.photoDialogRow.inspectionApplyCode || this.inspectionApplyCode,
        sortNo: img.sortNo,
        imageCode: img.imageCode,
        imageUrl: img.imageUrl,
        imageDesc: img.imageDesc,
      }
      const saveRes = await this.$api
        .dfInspectionProducImageSaveApi(payload)
        .catch(() => null)
      if (!saveRes || saveRes.status !== 200) {
        this.$message.error(
          this.$l('tips_inspection_photo_save_fail') || '保存失败',
        )
      }
    },
    async saveInlineImageDesc(row, img) {
      if (this.disabled || !row || !img) return
      const payload = {
        inspectionProductImageCode: img.inspectionProductImageCode || '',
        inspectionProductDetailCode: row.inspectionProductDetailCode,
        inspectionProductCode: row.inspectionProductCode,
        inspectionApplyCode: row.inspectionApplyCode || this.inspectionApplyCode,
        sortNo: img.sortNo,
        imageCode: img.imageCode,
        imageUrl: img.imageUrl,
        imageDesc: img.imageDesc,
      }
      const saveRes = await this.$api
        .dfInspectionProducImageSaveApi(payload)
        .catch(() => null)
      if (!saveRes || saveRes.status !== 200) {
        this.$message.error(
          this.$l('tips_inspection_photo_save_fail') || '保存失败',
        )
      }
    },
    openInlineImageDescDialog(row, img) {
      if (this.disabled || !row || !img) return
      this.inlineDescRow = row
      this.inlineDescImg = img
      this.inlineDescText = img.imageDesc || ''
      this.inlineDescDialogVisible = true
    },
    async confirmInlineImageDesc() {
      if (this.disabled || !this.inlineDescRow || !this.inlineDescImg) return
      const nextDesc = this.inlineDescText || ''
      if (nextDesc.length > 100) {
        this.$message.warning(
          this.$l('tips_max_length_100') || '最多输入100个字符',
        )
        return
      }
      if (nextDesc === (this.inlineDescImg.imageDesc || '')) {
        this.inlineDescDialogVisible = false
        return
      }
      this.inlineDescImg.imageDesc = nextDesc
      await this.saveInlineImageDesc(this.inlineDescRow, this.inlineDescImg)
      this.inlineDescDialogVisible = false
    },
    onInlineDescDialogClosed() {
      this.inlineDescText = ''
      this.inlineDescRow = null
      this.inlineDescImg = null
    },
    async removeDialogImage(img) {
      if (this.disabled) return
      const code = img.inspectionProductImageCode
      if (!code) {
        this.dialogImageList = this.dialogImageList.filter((i) => i !== img)
        return
      }
      const ok = await this.$confirm(
        this.$l('tips_inspection_photo_del_confirm') || '确定删除该照片？',
        this.$l('dialog_title_confirm') || '确认信息',
        {
          type: 'warning',
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
        },
      )
        .then(() => true)
        .catch(() => false)
      if (!ok) return
      const res = await this.$api
        .dfInspectionProductImageDelApi({ listInspectionProductImageCode: [code] })
        .catch(() => null)
      if (!res || res.status !== 200) {
        this.$message.error(
          this.$l('tips_inspection_photo_del_fail') || '删除失败',
        )
        return
      }
      this.dialogImageList = this.dialogImageList.filter(
        (i) => i.inspectionProductImageCode !== code,
      )
      this.$message.success(this.$l('tips_inspection_photo_del_ok') || '删除成功')
      this.refreshDialogImagesFromServer()
    },
    async refreshDialogImagesFromServer() {
      const row = this.photoDialogRow
      if (!row || !row.inspectionProductCode) return
      const listRes = await this.$api
        .dfInspectionProductDetailReportListApi({
          inspectionProductCode: row.inspectionProductCode,
        })
        .catch(() => null)
      if (
        !this.ensureOk(
          listRes,
          this.$l('tips_inspection_photo_list_fail') || '获取产品照片失败',
        )
      ) {
        return
      }
      const rows = this.parseListData(listRes)
      const fresh = rows.find(
        (r) => r.inspectionProductDetailCode === row.inspectionProductDetailCode,
      )
      if (fresh && fresh.imageList) {
        this.dialogImageList = JSON.parse(JSON.stringify(fresh.imageList))
        this.photoDialogRow = { ...fresh }
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.singlePhotoUploadTimer)
  },
}
</script>

<style lang="scss" scoped>
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.section-head {
  padding-bottom: 8px;
}
.text-muted {
  color: #909399;
}
.small {
  font-size: 12px;
}
.product-block {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 0;
  background: #fff;
}
.product-info-form ::v-deep .el-form-item {
  margin-bottom: 0;
}
.product-info-form {
  border-bottom: 1px solid #ebeef5;
}
.info-grid-row {
  margin: 0 !important;
  display: flex;
  align-items: stretch;
}
.info-grid-cell {
  padding: 0 !important;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}
.info-grid-row .info-grid-cell:last-child {
  border-right: none;
}
.product-info-form ::v-deep .el-form-item {
  display: flex;
  align-items: stretch;
  min-height: 40px;
  width: 100%;
  height: 100%;
  flex: 1;
}
.product-info-form ::v-deep .el-form-item__label {
  text-align: left;
  background: #f5f7fa;
  color: #606266;
  padding: 0 12px;
  line-height: 20px;
  min-height: 40px;
  height: auto;
  display: flex;
  align-items: center;
  border-right: none;
}
.product-info-form ::v-deep .el-form-item__content {
  margin-left: 0 !important;
  flex: 1;
  background: #fff;
  padding: 0 12px;
  line-height: 20px;
  min-height: 40px;
  text-align: left;
  display: flex;
  align-items: center;
  height: auto;
}
.product-info-form ::v-deep .el-form-item__label,
.product-info-form ::v-deep .el-form-item__content {
  box-sizing: border-box;
  height: 100%;
}
.product-info-form ::v-deep .el-form-item__content .el-button {
  margin-left: 0;
}
.id-value-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
}
.id-value-row > span {
  flex: 1;
  min-width: 0;
}
.id-value-row > .el-button {
  margin-left: auto;
}
.photo-strip {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  margin-top: 8px;
  padding: 10px 12px 12px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.photo-item {
  flex: 0 0 235px;
  width: 235px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.photo-thumb {
  width: 210px;
  height: 190px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}
.photo-desc {
  margin-top: 6px;
  width: 210px;
  cursor: pointer;
  text-align: center;
}
.photo-dialog-grid {
  gap: 16px;
  overflow-y: auto;
}
.photo-dialog-item {
  width: 200px;
}
.photo-dialog-img-wrap {
  position: relative;
  margin-bottom: 6px;
}
.photo-dialog-img {
  width: 200px;
  height: 150px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}
.photo-del-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
  background: #f56c6c;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  z-index: 2;
}
.photo-del-btn:hover {
  background: #f78989;
}
.d-none {
  display: none !important;
}
.empty-hint {
  font-size: 13px;
}
::v-deep .inspection-photo-dialog.el-dialog {
  height: 650px;
  display: flex;
  flex-direction: column;
}
::v-deep .inspection-photo-dialog.el-dialog .el-dialog__body {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
::v-deep .inspection-photo-dialog.el-dialog .photo-dialog-grid {
  flex: 0 0 auto;
  box-sizing: border-box;
  padding-bottom: 24px;
}
::v-deep .inspection-photo-dialog.el-dialog .photo-dialog-toolbar {
  flex: 0 0 auto;
}
</style>
